import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FEEDBACK_MODES } from "@/lib/checkins";
import { errMsg } from "../staff/ResolveDialog";
import { DueDatePicker, todayMelbourne } from "./DueDatePicker";

const METHODS = { verbal: "Verbal (in clinic / phone)", written: "Written / signed", email: "By email", portal: "Client portal" };

export function ConsentFields({ wording, consent, setConsent }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 space-y-2" data-testid="checkin-consent">
      <label className="flex items-start gap-2 text-sm">
        <Checkbox checked={consent.given} onCheckedChange={(v) => setConsent({ ...consent, given: !!v })} data-testid="checkin-consent-checkbox" className="mt-0.5" />
        <span>Client has explicitly agreed: <em>“{wording}”</em></span>
      </label>
      <Select value={consent.method} onValueChange={(v) => setConsent({ ...consent, method: v })}>
        <SelectTrigger className="h-8 text-xs" data-testid="checkin-consent-method"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(METHODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export function EnrolDialog({ email, clientName, canonicalId, templates, staff, me, wording, onClose, onDone }) {
  const usable = templates.filter((t) => t.current_version_id);
  const [f, setF] = useState({ template_id: usable[0]?.id || "", cadence_days: 28, start_date: todayMelbourne(), feedback_mode: "hybrid",
    practitioner_id: me?.id || "", preferred_name: (clientName || "").split(" ")[0] });
  const [consent, setConsent] = useState({ given: false, method: "verbal" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  async function save() {
    setBusy(true);
    try {
      await api.post("/checkin-enrolments", { ...f, client_email: email, client_name: clientName, canonical_client_id: canonicalId || null, consent });
      toast.success("Client enrolled", { description: `First invitation goes at 9:00 am on ${f.start_date}.` });
      onDone();
      onClose();
    } catch (e) {
      toast.error("Couldn't enrol", { description: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="checkin-enrol-dialog">
        <DialogHeader>
          <DialogTitle>Enrol in a clinical progress review</DialogTitle>
          <DialogDescription>Invitations are emailed at 9:00 am (Melbourne) on each due date, with one reminder after 3 days.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div><Label>Template</Label>
            <Select value={f.template_id} onValueChange={set("template_id")}>
              <SelectTrigger data-testid="checkin-enrol-template"><SelectValue placeholder="Choose a template" /></SelectTrigger>
              <SelectContent>{usable.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Cadence</Label>
              <Select value={String(f.cadence_days)} onValueChange={(v) => set("cadence_days")(Number(v))}>
                <SelectTrigger data-testid="checkin-enrol-cadence"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="28">Every 4 weeks</SelectItem><SelectItem value="42">Every 6 weeks</SelectItem></SelectContent>
              </Select>
              <p className="mt-1 text-2xs text-zinc-500">4 weeks for new or monitored clients, 6 for stable.</p>
            </div>
            <div><Label>First due date</Label><DueDatePicker value={f.start_date} onChange={set("start_date")} testId="checkin-enrol-start" /></div>
          </div>
          <div><Label>Feedback release</Label>
            <Select value={f.feedback_mode} onValueChange={set("feedback_mode")}>
              <SelectTrigger data-testid="checkin-enrol-feedback-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(FEEDBACK_MODES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Practitioner</Label>
              <Select value={f.practitioner_id} onValueChange={set("practitioner_id")}>
                <SelectTrigger data-testid="checkin-enrol-practitioner"><SelectValue /></SelectTrigger>
                <SelectContent>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Greeting name</Label><Input value={f.preferred_name} onChange={(e) => set("preferred_name")(e.target.value)} data-testid="checkin-enrol-preferred-name" /></div>
          </div>
          <ConsentFields wording={wording} consent={consent} setConsent={setConsent} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !consent.given || !f.template_id} data-testid="checkin-enrol-save">Enrol client</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
